import {
  listBroadcasts,
  listAgendaItem,
  listTracks,
  Track,
  destroyBroadcasts,
  destroyAgendaItem
} from '@goldcast/api/backend';

import { AgendaTrack, Broadcast, AgendaItem } from './types';
import { updateAgendaItemInTracks, removeAgendaItemFromTracks } from './utils';

import router from '@/router';
import { socket } from '@/store/modules/user';
import { processErrorParent } from '@/utils/utils';

const actions = {
  async fetchAllBroadcasts(context, { eventId }: { eventId: string }) {
    context.commit('setBroadcastLoading', true);
    try {
      const broadcasts = await listBroadcasts({
        queryParams: {
          event: eventId
        }
      });
      context.commit('setBroadcasts', broadcasts);
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Error fetching broadcasts, Please Try Again', mode: 'error' },
        { root: true }
      );
      context.commit('setErrorFetchingBroadcasts', error);
    } finally {
      context.commit('setBroadcastLoading', false);
    }
  },
  async fetchAllAgendaItems(context, { eventId }: { eventId: string }) {
    context.commit('setAgendaItemsLoading', true);
    try {
      const agendaItems = await listAgendaItem({
        queryParams: {
          event: eventId
        }
      });
      context.commit('setAgendaItems', agendaItems);
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Error fetching agenda items, Please Try Again', mode: 'error' },
        { root: true }
      );
      context.commit('setErrorFetchingAgendaItems', error);
    } finally {
      context.commit('setAgendaItemsLoading', false);
    }
  },
  async fetchAgendaTracks(context, { eventId }: { eventId: string }) {
    context.commit('setAgendaTracksLoading', true);
    try {
      const agendaTracks = (await listTracks({
        queryParams: {
          event: eventId
        }
      })) as Track[];

      const sortedTracks = agendaTracks.sort((a, b) => {
        return (a?.sort_priority ?? 0) - (b?.sort_priority ?? 0);
      });
      context.commit('setAgendaTracks', sortedTracks);
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'Error fetching agenda tracks, Please Try Again',
          mode: 'error'
        },
        { root: true }
      );
    } finally {
      context.commit('setAgendaTracksLoading', false);
    }
  },
  async createAgendaTrack(context, { track }: { track: AgendaTrack }) {
    try {
      const currentTracks = context.state.agendaTracks;
      const newTrack = await context.dispatch(
        'makeapicall',
        {
          url: `/event/tracks/`,
          method: 'POST',
          body: JSON.stringify(track)
        },
        {
          root: true
        }
      );
      const allTracks = [...currentTracks, newTrack];
      context.commit('setAgendaTracks', allTracks);
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: `New Track Added - ${newTrack.title}`,
          mode: 'success'
        },
        { root: true }
      );
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'Failed to add track, please try again',
          mode: 'error'
        },
        { root: true }
      );
    }
  },
  async updateAgendaTrack(context, { track }: { track: AgendaTrack }) {
    const currentTracks = context.state.agendaTracks;
    try {
      const updatedTrack = await context.dispatch(
        'makeapicall',
        {
          url: `/event/tracks/${track.id}/`,
          method: 'PATCH',
          body: JSON.stringify(track)
        },
        {
          root: true
        }
      );
      const newTracks = currentTracks.map(currentTrack => (currentTrack.id === track.id ? updatedTrack : currentTrack));
      context.commit('setAgendaTracks', newTracks);
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'Track updated successfully',
          mode: 'success'
        },
        { root: true }
      );
    } catch (error) {
      context.commit('setAgendaTracks', currentTracks);
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'Failed to update track, please try again',
          mode: 'error'
        },
        { root: true }
      );
    }
  },
  async testSumiliveSession(context, { broadcast }: { broadcast: Broadcast }) {
    const { id: broadcastId } = broadcast;
    const stoppingTest = broadcast.simulive_test_run_end_time;

    let url = `/event/broadcasts/${broadcastId}/test_simulive/`;

    let testingSuccessMessage = 'Testing for prerecorded session will start in 2-3 minutes!';
    let testingFailureMessage = 'Testing could not start for prerecorded session! Please retry again in 2-3 minutes.';
    let successState = 'STARTING';

    if (stoppingTest) {
      url = `/event/broadcasts/${broadcastId}/stop_simulive_test/`;
      testingSuccessMessage = 'Testing of the prerecorded session has stopped!';
      testingFailureMessage = 'Error while stopping the test run of the prerecorded session!';
      successState = 'STOPPING';
    }

    // const currentBroadcastMediaLiveChannel = broadcast.medialive_channel_state;
    const input = {
      id: broadcastId,
      payload: {
        test_simulive_processing_state: successState
      }
    };

    context.commit('updatePartialBroadcast', input);

    try {
      await context.dispatch(
        'makeapicall',
        {
          url,
          method: 'POST',
          body: JSON.stringify({})
        },
        {
          root: true
        }
      );
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: testingSuccessMessage,
          mode: 'success'
        },
        { root: true }
      );
    } catch (error) {
      context.commit('updatePartialBroadcast', {
        id: broadcastId,
        payload: {
          test_simulive_processing_state: undefined
        }
      });
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: testingFailureMessage,
          mode: 'error'
        },
        { root: true }
      );
    }
  },
  async addAgendaItem(context, { agendaItem }: { agendaItem: AgendaItem }) {
    try {
      const newAgendaItem = await context.dispatch(
        'makeapicall',
        {
          url: `/event/agenda-item/`,
          method: 'POST',
          body: JSON.stringify(agendaItem)
        },
        {
          root: true
        }
      );
      const tracks = context.state.agendaTracks;
      const updatedTracks = updateAgendaItemInTracks({ ...newAgendaItem, track: agendaItem.track || [] }, tracks);
      context.commit('setAgendaTracks', updatedTracks);
      const selectedTracks = context.state.selectedTrack;
      if (selectedTracks) {
        const updatedSelectedTracks = updateAgendaItemInTracks({ ...newAgendaItem, track: agendaItem.track || [] }, [
          selectedTracks
        ])[0];
        context.commit('setSelectedTrack', updatedSelectedTracks);
      }
      context.commit('setNewAgendaItem', newAgendaItem);
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'New Agenda Item created successfully',
          mode: 'success'
        },
        { root: true }
      );
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'Failed to save agenda item, please try again',
          mode: 'error'
        },
        { root: true }
      );
    }
  },
  async updateAgendaItem(context, { agendaItem }: { agendaItem: AgendaItem }) {
    try {
      await context.dispatch(
        'makeapicall',
        {
          url: `/event/agenda-item/${agendaItem.id}/`,
          method: 'PATCH',
          body: JSON.stringify(agendaItem)
        },
        {
          root: true
        }
      );
      const tracks = context.state.agendaTracks;
      const selectedTracks = context.state.selectedTrack;

      const updatedTracks = updateAgendaItemInTracks(agendaItem, tracks);
      if (selectedTracks) {
        const updatedSelectedTracks = updateAgendaItemInTracks(agendaItem, [selectedTracks])[0];
        context.commit('setSelectedTrack', updatedSelectedTracks);
      }
      context.commit('setAgendaTracks', updatedTracks);
      context.commit('setUpdatedAgendaItem', agendaItem);
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'Agenda Item updated successfully',
          mode: 'success'
        },
        { root: true }
      );
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'Failed to update agenda item, please try again',
          mode: 'error'
        },
        { root: true }
      );
    }
  },
  async saveBroadcastSession(context, { broadcast, eventId }: { broadcast: any; eventId: string }) {
    try {
      const newBroadcast = await context.dispatch(
        'makeapicall',
        {
          url: `/event/broadcasts/`,
          method: 'POST',
          body: JSON.stringify(broadcast)
        },
        {
          root: true
        }
      );
      const tracks = context.state.agendaTracks;
      const updatedTracks = updateAgendaItemInTracks({ ...newBroadcast, track: broadcast.tracks }, tracks);
      context.commit('setAgendaTracks', updatedTracks);

      const selectedTracks = context.state.selectedTrack;
      if (selectedTracks) {
        const updatedSelectedTracks = updateAgendaItemInTracks({ ...newBroadcast, track: broadcast.tracks }, [
          selectedTracks
        ])[0];
        context.commit('setSelectedTrack', updatedSelectedTracks);
      }

      context.commit('setNewBroadcast', newBroadcast);
      context.dispatch('alerts/getAlertCenterChecks', eventId, { root: true });
      context.dispatch('studioStaff/loadStaffMembers', { eventId }, { root: true });
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'Session created successfully',
          mode: 'success'
        },
        { root: true }
      );
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: processErrorParent(error) || 'Failed to save session, please try again',
          mode: 'error'
        },
        { root: true }
      );
    }
  },

  updateBroadcastSession(context, { broadcast }: { broadcast: any }) {
    return new Promise((resolve, reject) => {
      context
        .dispatch(
          'makeapicall',
          {
            url: `/event/broadcasts/${broadcast.id}/`,
            method: 'PATCH',
            body: JSON.stringify(broadcast)
          },
          { root: true }
        )
        .then(updatedBroadcast => {
          if (broadcast?.tracks) {
            const tracks = context.state.agendaTracks;
            const updatedTracks = updateAgendaItemInTracks({ ...updatedBroadcast, track: broadcast.tracks }, tracks);
            context.commit('setAgendaTracks', updatedTracks);

            const selectedTracks = context.state.selectedTrack;
            if (selectedTracks) {
              const updatedSelectedTracks = updateAgendaItemInTracks({ ...updatedBroadcast, track: broadcast.tracks }, [
                selectedTracks
              ])[0];
              context.commit('setSelectedTrack', updatedSelectedTracks);
            }
          }

          context.commit('setUpdatedBroadcast', updatedBroadcast);
          resolve(updatedBroadcast);
        })
        .catch(error => {
          reject(error);
        });
    });
  },
  async deleteBroadcast(context, { broadcastId }: { broadcastId: string }) {
    return new Promise((resolve, reject) => {
      destroyBroadcasts({
        id: broadcastId
      })
        .then(() => {
          const tracks = context.state.agendaTracks;
          context.commit('deleteBroadcast', broadcastId);
          const updatedTracks = removeAgendaItemFromTracks(broadcastId, tracks);
          context.commit('setAgendaTracks', updatedTracks);
          resolve(broadcastId);
        })
        .catch(reject);
    });
  },
  async deleteAgendaItem(context, { agendaItemId }: { agendaItemId: string }) {
    return new Promise((resolve, reject) => {
      destroyAgendaItem({
        id: Number(agendaItemId)
      })
        .then(() => {
          const tracks = context.state.agendaTracks;
          context.commit('deleteAgendaItem', agendaItemId);
          const updatedTracks = removeAgendaItemFromTracks(agendaItemId, tracks);
          context.commit('setAgendaTracks', updatedTracks);
          resolve(agendaItemId);
        })
        .catch(reject);
    });
  },
  async broadcastGatewaySocketEvents(context, msg: { data: { broadcast: Broadcast } }) {
    context.commit('updatePartialBroadcast', {
      id: msg.data.broadcast.id,
      payload: {
        is_live: msg.data.broadcast.is_live,
        simulive_test_run_end_time: msg.data.broadcast.simulive_test_run_end_time
      }
    });
  },
  bulkUpdateAgendaTracks(context, { agendaTracks, eventId }: { agendaTracks: AgendaTrack[]; eventId: string }) {
    const payload = {
      event: eventId,
      tracks: agendaTracks
    };

    return new Promise((resolve, reject) => {
      context
        .dispatch(
          'makeapicall',
          {
            url: `/event/tracks/bulk/`,
            method: 'PUT',
            body: JSON.stringify(payload)
          },
          {
            root: true
          }
        )
        .then(tracks => {
          context.commit('setAgendaTracks', tracks);
          resolve(tracks);
        })
        .catch(error => {
          reject(error);
        });
    });
  },
  processSocketOpenDone(context) {
    const eventID =
      (router.currentRoute as any).value.params?.eventId || (router.currentRoute as any).value.params?.seriesId;

    if (!eventID) {
      return;
    }

    socket.sendMessageWithConnection({
      message: 'setEventId',
      eventId: eventID,
      userId: context.rootState.user?.currentUser?.id,
      route: (router.currentRoute as any).value.fullPath
    });
  }
};

export default actions;
